<template>
  <div class="question-submission">
    <!-- Limit Reached Message -->
    <div v-if="limitReached" class="limit-message">
      <span class="limit-icon">⚠️</span>
      <h3>Submission Limit Reached</h3>
      <p>
        Because of the number of questions submitted at this time we are not accepting more questions. Please check back again later.
      </p>
    </div>

    <div class="submission-tabs">
      <button 
        :class="['tab-button', { active: activeTab === 'text' }]"
        @click="activeTab = 'text'"
        aria-label="Submit text question"
        :disabled="limitReached"
      >
        <span class="tab-icon">✍️</span>
        Text Question
        <span v-if="limitReached" class="tab-disabled">(Full)</span>
      </button>
      <button 
        :class="['tab-button', { active: activeTab === 'audio' }]"
        @click="activeTab = 'audio'"
        aria-label="Record audio question"
        :disabled="limitReached"
      >
        <span class="tab-icon">🎤</span>
        Audio Question
        <span v-if="limitReached" class="tab-disabled">(Full)</span>
      </button>
    </div>

    <!-- Text Question Form -->
    <form v-if="activeTab === 'text' && !limitReached" @submit.prevent="submitTextQuestion" class="question-form">
      <div class="form-group">
        <label for="listener-name">Your Name</label>
        <input 
          id="listener-name"
          v-model="textForm.name"
          type="text"
          required
          placeholder="Enter your name"
          maxlength="100"
        />
      </div>

      <div class="form-group">
        <label for="listener-email">Email (optional)</label>
        <input 
          id="listener-email"
          v-model="textForm.email"
          type="email"
          placeholder="your@email.com"
        />
      </div>

      <div class="form-group">
        <label for="question-text">Your Question</label>
        <textarea 
          id="question-text"
          v-model="textForm.question"
          required
          placeholder="Type your question here..."
          rows="6"
          maxlength="1000"
        ></textarea>
        <span class="char-count">{{ textForm.question.length }}/1000</span>
      </div>

      <!-- Turnstile CAPTCHA -->
      <div class="turnstile-container">
        <div id="turnstile-text"></div>
      </div>

      <div class="form-actions">
        <button 
          type="submit" 
          class="submit-button"
          :disabled="isSubmitting || !turnstileToken"
        >
          {{ isSubmitting ? 'Submitting...' : 'Submit Question' }}
        </button>
      </div>
    </form>

    <!-- Audio Recording Form -->
    <div v-if="activeTab === 'audio' && !limitReached" class="audio-form">
      <!-- iOS Compatibility Notice -->
      <div v-if="!isDevelopmentMode" class="compatibility-notice">
        <span class="notice-icon">ℹ️</span>
        <p>
          <strong>Note for iPhone/iPad users:</strong> If audio recording doesn't work, please try using a text question instead. 
          Older iOS versions (before iOS 14.3) have limited audio recording support in browsers.
        </p>
      </div>

      <div class="form-group">
        <label for="audio-name">Your Name</label>
        <input 
          id="audio-name"
          v-model="audioForm.name"
          type="text"
          required
          placeholder="Enter your name"
          maxlength="100"
        />
      </div>

      <div class="form-group">
        <label for="audio-email">Email (optional)</label>
        <input 
          id="audio-email"
          v-model="audioForm.email"
          type="email"
          placeholder="your@email.com"
        />
      </div>

      <div v-if="availableMicrophones.length > 1" class="form-group">
        <label for="microphone-select">Microphone</label>
        <select 
          id="microphone-select"
          v-model="selectedMicrophoneId"
          :disabled="isRecording"
        >
          <option 
            v-for="mic in availableMicrophones" 
            :key="mic.deviceId"
            :value="mic.deviceId"
          >
            {{ mic.label }}
          </option>
        </select>
      </div>

      <div class="recording-section">
        <div class="recording-controls">
          <button 
            v-if="!isRecording && !audioBlob"
            @click="startRecording"
            class="record-button"
            type="button"
            :disabled="!micPermission"
          >
            <span class="record-icon">🎤</span>
            Start Recording
          </button>

          <button 
            v-if="isRecording"
            @click="stopRecording"
            class="stop-button"
            type="button"
          >
            <span class="stop-icon">⏹️</span>
            Stop Recording
          </button>

          <button 
            v-if="audioBlob && !isRecording"
            @click="resetRecording"
            class="reset-button"
            type="button"
          >
            <span class="reset-icon">🔄</span>
            Record Again
          </button>
        </div>

        <div v-if="isRecording" class="recording-indicator">
          <span class="pulse-dot"></span>
          Recording... {{ recordingDuration }}s
        </div>

        <!-- Audio Level Meter -->
        <div v-if="isRecording" class="audio-meter-container">
          <div class="audio-meter-label">
            <span>Audio Level</span>
            <span v-if="isClipping" class="clipping-warning">⚠️ CLIPPING!</span>
            <span v-else-if="isTooQuiet" class="quiet-warning">🔇 Too Quiet</span>
            <span v-else class="level-ok">✓ Good Level</span>
          </div>
          <div class="audio-meter-track">
            <div 
              class="audio-meter-fill" 
              :class="{ 'clipping': isClipping, 'quiet': isTooQuiet }"
              :style="{ width: audioLevel + '%' }"
            ></div>
            <div class="audio-meter-marks">
              <span class="mark quiet-threshold"></span>
              <span class="mark optimal-start"></span>
              <span class="mark optimal-end"></span>
              <span class="mark clip-threshold"></span>
            </div>
          </div>
          <div class="audio-meter-scale">
            <span>-60dB</span>
            <span>-40dB</span>
            <span>-20dB</span>
            <span>-12dB</span>
            <span>0dB</span>
          </div>
        </div>

        <div v-if="!micPermission && !isRecording" class="permission-message">
          <p>🎤 Microphone access is required to record audio questions.</p>
          <button @click="requestMicPermission" class="permission-button" type="button">
            Enable Microphone
          </button>
        </div>

        <div v-if="audioBlob" class="audio-preview">
          <label>Preview Your Recording:</label>
          <audio 
            ref="audioPlayer"
            :src="audioUrl" 
            controls 
            class="audio-player"
            @loadstart="onAudioLoadStart"
            @loadedmetadata="onAudioLoadedMetadata"
            @loadeddata="onAudioLoadedData"
            @canplay="onAudioCanPlay"
            @canplaythrough="onAudioCanPlayThrough"
            @play="onAudioPlay"
            @pause="onAudioPause"
            @ended="onAudioEnded"
            @error="onAudioError"
          ></audio>
          <p class="audio-info">
            Duration: {{ recordingDuration }}s | 
            Size: {{ formatFileSize(audioBlob.size) }} | 
            Type: {{ audioBlob.type || 'unknown' }}
          </p>
          <p class="audio-debug" style="font-size: 0.8rem; color: #666; font-style: italic;">
            ℹ️ If you can't hear audio in the preview above, the recording may have failed. Try recording again or use a text question instead.
          </p>
        </div>
      </div>

      <!-- Turnstile CAPTCHA -->
      <div class="turnstile-container">
        <div id="turnstile-audio"></div>
      </div>

      <div class="form-actions">
        <button 
          @click="submitAudioQuestion"
          class="submit-button"
          :disabled="!audioBlob || isSubmitting || !turnstileTokenAudio"
          type="button"
        >
          {{ isSubmitting ? 'Uploading...' : 'Submit Audio Question' }}
        </button>
      </div>
    </div>

    <!-- Development Mode Notice -->
    <div v-if="isDevelopmentMode" class="dev-notice">
      🔧 Development Mode - Submissions will be logged to console
    </div>

    <!-- Success Message -->
    <div v-if="showSuccess" class="success-message">
      <span class="success-icon">✅</span>
      <h3>Thank you for your question!</h3>
      <p>We'll review it and may answer it on air.</p>
      <p v-if="isDevelopmentMode" class="dev-note">
        (Development mode: Check browser console for details)
      </p>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="error-message">
      <span class="error-icon">⚠️</span>
      <p>{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuestionSubmission',
  data() {
    return {
      activeTab: 'text',
      isDevelopmentMode: process.env.NODE_ENV === 'development',
      questionLimits: null,
      turnstileToken: null,
      turnstileTokenAudio: null,
      turnstileWidgetId: null,
      turnstileWidgetIdAudio: null,
      turnstileRendered: false,
      textForm: {
        name: '',
        email: '',
        question: ''
      },
      audioForm: {
        name: '',
        email: ''
      },
      isRecording: false,
      isSubmitting: false,
      showSuccess: false,
      errorMessage: '',
      mediaRecorder: null,
      audioChunks: [],
      audioBlob: null,
      audioUrl: null,
      recordingDuration: 0,
      recordingTimer: null,
      micPermission: false,
      audioContext: null,
      audioAnalyser: null,
      audioLevelCheckInterval: null,
      availableMicrophones: [],
      selectedMicrophoneId: null,
      audioLevel: 0,
      isClipping: false,
      isTooQuiet: false,
      levelAnimationFrame: null
    };
  },
  computed: {
    limitReached() {
      // Client-side uses UI limit from server (soft limit)
      // Server enforces actual QUESTION_LIMIT (hard limit)
      if (!this.questionLimits) return false;
      const uiLimit = this.questionLimits.total.uiLimit || (this.questionLimits.total.limit - 5);
      return this.questionLimits.total.count >= uiLimit;
    }
  },
  methods: {
    checkAudioCompatibility() {
      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        console.warn('⚠️ MediaRecorder not supported on this device');
        return;
      }

      console.log('🔍 Audio Recording Compatibility Check:');
      console.log('Browser:', navigator.userAgent);
      console.log('MediaRecorder supported:', !!window.MediaRecorder);

      // Log supported mime types for debugging
      const testTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/mpeg'
      ];

      console.log('Supported formats:');
      testTypes.forEach(type => {
        const supported = MediaRecorder.isTypeSupported ? MediaRecorder.isTypeSupported(type) : false;
        console.log(`  ${type}: ${supported ? '✅' : '❌'}`);
      });

      // Detect iOS version for specific warnings
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (iOS) {
        const version = navigator.userAgent.match(/OS (\d+)_(\d+)/);
        if (version) {
          const majorVersion = parseInt(version[1]);
          const minorVersion = parseInt(version[2]);
          console.log(`📱 iOS ${majorVersion}.${minorVersion} detected`);
          if (majorVersion < 14 || (majorVersion === 14 && minorVersion < 3)) {
            console.warn(`⚠️ iOS ${majorVersion}.${minorVersion} may have limited audio recording support. iOS 14.3+ recommended.`);
          }
        }
      }
    },
    async checkQuestionLimits() {
      if (this.isDevelopmentMode) {
        // In development mode, no limits
        this.questionLimits = {
          total: { count: 0, limit: 20, available: 20, canSubmit: true }
        };
        return;
      }

      try {
        const response = await fetch('/api/question-status');
        if (response.ok) {
          this.questionLimits = await response.json();
        }
      } catch (error) {
        console.error('Failed to check question limits:', error);
        // Default to allowing submissions if check fails
        this.questionLimits = {
          total: { count: 0, limit: 20, available: 20, canSubmit: true }
        };
      }
    },
    async requestMicPermission() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.micPermission = true;
        stream.getTracks().forEach(track => track.stop());
      } catch (error) {
        this.errorMessage = 'Microphone access denied. Please enable microphone permissions in your browser settings.';
      }
    },
    async startRecording() {
      try {
        const audioConstraints = {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        };
        
        // Add deviceId constraint if a specific microphone is selected
        if (this.selectedMicrophoneId) {
          audioConstraints.deviceId = { exact: this.selectedMicrophoneId };
        }
        
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: audioConstraints
        });
        
        console.log('🎙️ Audio stream tracks:', stream.getAudioTracks().map(track => ({
          label: track.label,
          enabled: track.enabled,
          muted: track.muted,
          settings: track.getSettings()
        })));
        
        // Now that we have permission, enumerate microphones to get device labels
        if (this.availableMicrophones.length === 0 || !this.availableMicrophones[0].label.includes('Microphone')) {
          await this.enumerateMicrophones();
        }
        
        // Set recording flag BEFORE setting up audio monitoring
        // so the monitoring loop doesn't immediately clean up
        this.isRecording = true;
        
        // Set up audio level monitoring
        this.setupAudioLevelMonitoring(stream);
        
        // Check for MediaRecorder support
        if (!window.MediaRecorder) {
          throw new Error('MediaRecorder not supported on this device');
        }

        // Explicitly try supported formats in order of preference
        let options = {};
        let selectedMimeType = '';
        
        const preferredTypes = [
          'audio/webm;codecs=opus',
          'audio/webm',
          'audio/mp4'
        ];
        
        for (const type of preferredTypes) {
          if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type)) {
            options = { mimeType: type };
            selectedMimeType = type;
            console.log('✅ Selected mime type:', type);
            break;
          }
        }
        
        // Create MediaRecorder with explicit options if we found a supported type
        this.mediaRecorder = options.mimeType ? new MediaRecorder(stream, options) : new MediaRecorder(stream);
        this.audioChunks = [];
        this.recordingDuration = 0;
        
        // Store the actual mime type being used
        const actualMimeType = this.mediaRecorder.mimeType || selectedMimeType || 'audio/webm';
        
        console.log('🎙️ MediaRecorder created:', {
          requestedMimeType: selectedMimeType,
          actualMimeType: this.mediaRecorder.mimeType,
          finalMimeType: actualMimeType,
          state: this.mediaRecorder.state
        });

        this.mediaRecorder.ondataavailable = (event) => {
          console.log('📦 Data chunk received:', event.data.size, 'bytes');
          if (event.data && event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          console.log('⏹️ Recording stopped. Total chunks:', this.audioChunks.length);
          
          if (this.audioChunks.length === 0) {
            console.error('❌ No audio chunks recorded!');
            this.errorMessage = 'Recording failed - no audio data captured. Please try again.';
            stream.getTracks().forEach(track => track.stop());
            return;
          }
          
          // Use the actual mime type from the recorder, fallback to selected type
          const blobMimeType = this.mediaRecorder.mimeType || actualMimeType;
          console.log('📝 Creating blob with type:', blobMimeType);
          
          this.audioBlob = new Blob(this.audioChunks, { type: blobMimeType });
          this.audioUrl = URL.createObjectURL(this.audioBlob);
          
          console.log('✅ Audio blob created:', {
            size: this.audioBlob.size,
            type: this.audioBlob.type,
            chunks: this.audioChunks.length,
            url: this.audioUrl ? 'created' : 'failed'
          });
          
          // Log individual chunk sizes to diagnose issues
          console.log('📊 Chunk sizes:', this.audioChunks.map(chunk => chunk.size));
          
          stream.getTracks().forEach(track => track.stop());
          
          // Final validation
          if (this.audioBlob.size === 0) {
            console.error('❌ Audio blob is empty!');
            this.errorMessage = 'Recording failed - no audio data captured. Please try again.';
            this.resetRecording();
            return;
          }
          
          // Try to verify the blob is playable by testing with a temp audio element
          this.verifyAudioPlayback();
        };

        this.mediaRecorder.onerror = (event) => {
          console.error('❌ MediaRecorder error:', event);
          this.errorMessage = 'Recording error occurred. Please try again.';
          this.isRecording = false;
          stream.getTracks().forEach(track => track.stop());
        };

        this.mediaRecorder.onstart = () => {
          console.log('▶️ Recording started');
        };

        // Start recording - request data periodically to ensure we get chunks
        this.mediaRecorder.start(1000); // Request data every second
        this.micPermission = true;

        // Start duration timer
        this.recordingTimer = setInterval(() => {
          this.recordingDuration++;
          // Auto-stop at 3 minutes
          if (this.recordingDuration >= 180) {
            this.stopRecording();
          }
        }, 1000);
        
        console.log('✅ Recording setup complete');
        
      } catch (error) {
        let errorMsg = 'Unable to access microphone. ';
        
        if (error.name === 'NotAllowedError') {
          errorMsg += 'Please enable microphone permissions in your browser settings.';
        } else if (error.name === 'NotFoundError') {
          errorMsg += 'No microphone found on your device.';
        } else if (error.message.includes('not supported')) {
          errorMsg += 'Audio recording is not supported on this device/browser. Please try using a text question instead.';
        } else {
          errorMsg += 'Please check your browser permissions and try again.';
        }
        
        this.errorMessage = errorMsg;
        console.error('❌ Recording error:', error);
      }
    },
    
    setupAudioLevelMonitoring(stream) {
      try {
        // Create audio context and analyser
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.audioAnalyser = this.audioContext.createAnalyser();
        this.audioAnalyser.fftSize = 256;
        
        const source = this.audioContext.createMediaStreamSource(stream);
        source.connect(this.audioAnalyser);
        
        const bufferLength = this.audioAnalyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        
        console.log('🎚️ Audio level monitoring started');
        
        // Update audio levels in real-time
        const updateLevel = () => {
          if (!this.isRecording) {
            this.cleanupAudioLevelMonitoring();
            return;
          }

          this.audioAnalyser.getByteFrequencyData(dataArray);
          
          // Calculate RMS (Root Mean Square) for more accurate level
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            sum += dataArray[i] * dataArray[i];
          }
          const rms = Math.sqrt(sum / bufferLength);
          
          // Convert to percentage (0-100)
          this.audioLevel = Math.min(100, (rms / 128) * 100);

          // Detect clipping (>90% = -1dB or higher)
          this.isClipping = this.audioLevel > 90;
          
          // Detect too quiet (<15% = below -40dB)
          this.isTooQuiet = this.audioLevel < 15 && this.recordingDuration > 2; // Give 2 seconds to start talking

          this.levelAnimationFrame = requestAnimationFrame(updateLevel);
        };

        updateLevel();
        
      } catch (error) {
        console.error('❌ Failed to set up audio monitoring:', error);
      }
    },

    cleanupAudioLevelMonitoring() {
      if (this.levelAnimationFrame) {
        cancelAnimationFrame(this.levelAnimationFrame);
        this.levelAnimationFrame = null;
      }
      if (this.audioContext) {
        this.audioContext.close();
        this.audioContext = null;
      }
      this.audioAnalyser = null;
      this.audioLevel = 0;
      this.isClipping = false;
      this.isTooQuiet = false;
    },
    
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
        clearInterval(this.recordingTimer);
        
        // Clean up audio level monitoring
        this.cleanupAudioLevelMonitoring();
      }
    },
    resetRecording() {
      if (this.audioUrl) {
        URL.revokeObjectURL(this.audioUrl);
      }
      this.audioBlob = null;
      this.audioUrl = null;
      this.audioChunks = [];
      this.recordingDuration = 0;
    },
    async submitTextQuestion() {
      this.isSubmitting = true;
      this.errorMessage = '';
      this.showSuccess = false;

      try {
        // Development mode - log to console instead of API call
        if (this.isDevelopmentMode) {
          console.log('📝 Text Question Submitted (Development Mode):', {
            type: 'text',
            name: this.textForm.name,
            email: this.textForm.email,
            question: this.textForm.question,
            timestamp: new Date().toISOString()
          });
          
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 500));
          
          this.showSuccess = true;
          this.textForm = { name: '', email: '', question: '' };
          
          setTimeout(() => {
            this.showSuccess = false;
          }, 5000);
          
          return;
        }

        const response = await fetch('/api/submit-question', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            type: 'text',
            name: this.textForm.name,
            email: this.textForm.email,
            question: this.textForm.question,
            timestamp: new Date().toISOString(),
            turnstileToken: this.turnstileToken
          })
        });

        if (!response.ok) {
          throw new Error('Failed to submit question');
        }

        this.showSuccess = true;
        this.textForm = { name: '', email: '', question: '' };
        
        // Reset Turnstile
        this.resetTurnstile();
        
        // Refresh limits after successful submission
        await this.checkQuestionLimits();
        
        setTimeout(() => {
          this.showSuccess = false;
        }, 5000);
      } catch (error) {
        this.errorMessage = 'Failed to submit your question. Please try again.';
        console.error('Submission error:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    async submitAudioQuestion() {
      if (!this.audioBlob) return;
      
      // Validate audio blob has reasonable size (at least 1KB)
      if (this.audioBlob.size < 1000) {
        this.errorMessage = 'Recording appears to be empty or too short. Please try recording again.';
        console.error('❌ Audio blob too small:', this.audioBlob.size, 'bytes');
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = '';
      this.showSuccess = false;

      try {
        // Development mode - log to console instead of API call
        if (this.isDevelopmentMode) {
          console.log('🎤 Audio Question Submitted (Development Mode):', {
            type: 'audio',
            name: this.audioForm.name,
            email: this.audioForm.email,
            duration: this.recordingDuration,
            fileSize: this.formatFileSize(this.audioBlob.size),
            timestamp: new Date().toISOString()
          });
          console.log('Audio Blob:', this.audioBlob);
          
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          this.showSuccess = true;
          this.audioForm = { name: '', email: '' };
          this.resetRecording();
          
          setTimeout(() => {
            this.showSuccess = false;
          }, 5000);
          
          return;
        }

        const formData = new FormData();
        formData.append('type', 'audio');
        formData.append('name', this.audioForm.name);
        formData.append('email', this.audioForm.email);
        formData.append('turnstileToken', this.turnstileTokenAudio);
        
        // Determine file extension from blob type
        let extension = 'webm';
        const blobType = this.audioBlob.type || 'audio/webm';
        if (blobType.includes('mp4')) {
          extension = 'mp4';
        } else if (blobType.includes('mpeg') || blobType.includes('mp3')) {
          extension = 'mp3';
        } else if (blobType.includes('ogg')) {
          extension = 'ogg';
        } else if (blobType.includes('wav')) {
          extension = 'wav';
        }
        
        console.log('📤 Submitting audio:', {
          type: blobType,
          extension: extension,
          size: this.audioBlob.size
        });
        
        formData.append('audio', this.audioBlob, `question-${Date.now()}.${extension}`);
        formData.append('duration', this.recordingDuration);
        formData.append('timestamp', new Date().toISOString());

        const response = await fetch('/api/submit-question', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to upload audio question');
        }

        this.showSuccess = true;
        this.audioForm = { name: '', email: '' };
        this.resetRecording();
        
        // Reset Turnstile
        this.resetTurnstile();
        
        // Refresh limits after successful submission
        await this.checkQuestionLimits();
        
        setTimeout(() => {
          this.showSuccess = false;
        }, 5000);
      } catch (error) {
        this.errorMessage = 'Failed to upload your audio question. Please try again.';
        console.error('Upload error:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    
    // Audio player event handlers for debugging
    onAudioLoadStart(event) {
      console.log('🎵 Audio loadstart:', {
        src: event.target.src?.substring(0, 50) + '...',
        readyState: event.target.readyState,
        networkState: event.target.networkState
      });
    },
    onAudioLoadedMetadata(event) {
      console.log('🎵 Audio loadedmetadata:', {
        duration: event.target.duration,
        readyState: event.target.readyState,
        audioTracks: event.target.audioTracks?.length
      });
    },
    onAudioLoadedData(event) {
      console.log('🎵 Audio loadeddata:', {
        readyState: event.target.readyState,
        currentTime: event.target.currentTime
      });
    },
    onAudioCanPlay(event) {
      console.log('🎵 Audio canplay:', {
        readyState: event.target.readyState,
        duration: event.target.duration,
        paused: event.target.paused
      });
    },
    onAudioCanPlayThrough(event) {
      console.log('🎵 Audio canplaythrough:', {
        readyState: event.target.readyState,
        duration: event.target.duration
      });
    },
    onAudioPlay(event) {
      console.log('🎵 Audio play event:', {
        currentTime: event.target.currentTime,
        duration: event.target.duration,
        paused: event.target.paused,
        volume: event.target.volume,
        muted: event.target.muted
      });
    },
    onAudioPause(event) {
      console.log('🎵 Audio pause event:', {
        currentTime: event.target.currentTime
      });
    },
    onAudioEnded(event) {
      console.log('🎵 Audio ended event:', {
        currentTime: event.target.currentTime,
        duration: event.target.duration
      });
    },
    onAudioError(event) {
      const audio = event.target;
      console.error('❌ Audio error event:', {
        errorCode: audio.error?.code,
        errorMessage: audio.error?.message,
        src: audio.src?.substring(0, 50) + '...',
        readyState: audio.readyState,
        networkState: audio.networkState,
        errorDetails: {
          MEDIA_ERR_ABORTED: audio.error?.code === 1,
          MEDIA_ERR_NETWORK: audio.error?.code === 2,
          MEDIA_ERR_DECODE: audio.error?.code === 3,
          MEDIA_ERR_SRC_NOT_SUPPORTED: audio.error?.code === 4
        }
      });
    },
    
    verifyAudioPlayback() {
      console.log('🔍 Verifying audio playback capability...');
      
      // Create a test audio element to check if blob can be loaded
      const testAudio = new Audio();
      testAudio.src = this.audioUrl;
      
      testAudio.addEventListener('canplay', () => {
        console.log('✅ Audio blob CAN be played:', {
          duration: testAudio.duration,
          readyState: testAudio.readyState
        });
      }, { once: true });
      
      testAudio.addEventListener('error', () => {
        console.error('❌ Audio blob CANNOT be played:', {
          error: testAudio.error?.code,
          message: testAudio.error?.message
        });
      }, { once: true });
      
      // Try to load it
      testAudio.load();
    },
    
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
    
    loadTurnstile() {
      // Skip in development mode
      if (this.isDevelopmentMode) {
        console.log('🔧 Development mode - Turnstile verification disabled');
        this.turnstileToken = 'dev-mode-token';
        this.turnstileTokenAudio = 'dev-mode-token';
        return;
      }

      // Check if script already loaded
      if (window.turnstile) {
        this.renderTurnstileWidgets();
        return;
      }

      // Load Turnstile script
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('✅ Turnstile script loaded');
        this.renderTurnstileWidgets();
      };
      script.onerror = () => {
        console.error('❌ Failed to load Turnstile script');
        this.errorMessage = 'Failed to load security verification. Please refresh the page.';
      };
      document.head.appendChild(script);
    },
    
    renderTurnstileWidgets() {
      if (!window.turnstile) {
        console.log('⚠️ Turnstile not loaded yet');
        return;
      }

      // Render text question Turnstile (only if not already rendered)
      const textContainer = document.getElementById('turnstile-text');
      if (textContainer && !this.turnstileWidgetId) {
        try {
          // Check if container already has a widget
          if (textContainer.innerHTML.trim() !== '') {
            console.log('⚠️ Text Turnstile container already has content, skipping render');
            return;
          }
          
          const sitekey = process.env.VUE_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
          console.log('🔑 Rendering text Turnstile with sitekey type:', typeof sitekey);
          
          this.turnstileWidgetId = window.turnstile.render('#turnstile-text', {
            sitekey: sitekey,
            callback: (token) => {
              console.log('✅ Turnstile verified (text)');
              this.turnstileToken = token;
            },
            'error-callback': () => {
              console.error('❌ Turnstile error (text)');
              this.errorMessage = 'Security verification failed. Please try again.';
            },
            'expired-callback': () => {
              console.log('⏰ Turnstile expired (text)');
              this.turnstileToken = null;
            }
          });
          console.log('✅ Text Turnstile widget rendered, ID:', this.turnstileWidgetId);
        } catch (error) {
          console.error('❌ Failed to render text Turnstile:', error);
        }
      }

      // Render audio question Turnstile (only if not already rendered)
      const audioContainer = document.getElementById('turnstile-audio');
      if (audioContainer && !this.turnstileWidgetIdAudio) {
        try {
          // Check if container already has a widget
          if (audioContainer.innerHTML.trim() !== '') {
            console.log('⚠️ Audio Turnstile container already has content, skipping render');
            return;
          }
          
          const sitekey = process.env.VUE_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
          console.log('🔑 Rendering audio Turnstile with sitekey type:', typeof sitekey);
          
          this.turnstileWidgetIdAudio = window.turnstile.render('#turnstile-audio', {
            sitekey: sitekey,
            callback: (token) => {
              console.log('✅ Turnstile verified (audio)');
              this.turnstileTokenAudio = token;
            },
            'error-callback': () => {
              console.error('❌ Turnstile error (audio)');
              this.errorMessage = 'Security verification failed. Please try again.';
            },
            'expired-callback': () => {
              console.log('⏰ Turnstile expired (audio)');
              this.turnstileTokenAudio = null;
            }
          });
          console.log('✅ Audio Turnstile widget rendered, ID:', this.turnstileWidgetIdAudio);
        } catch (error) {
          console.error('❌ Failed to render audio Turnstile:', error);
        }
      }
    },
    
    resetTurnstile() {
      if (!window.turnstile) return;
      
      if (this.turnstileWidgetId) {
        window.turnstile.reset(this.turnstileWidgetId);
        this.turnstileToken = null;
      }
      
      if (this.turnstileWidgetIdAudio) {
        window.turnstile.reset(this.turnstileWidgetIdAudio);
        this.turnstileTokenAudio = null;
      }
    },
    
    async enumerateMicrophones() {
      try {
        // Enumerate devices without requesting permission yet
        // Note: Device labels will be blank until permission is granted
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.availableMicrophones = devices
          .filter(device => device.kind === 'audioinput')
          .map(device => ({
            deviceId: device.deviceId,
            label: device.label || `Microphone ${device.deviceId.substring(0, 8)}`
          }));
        
        console.log('🎤 Available microphones:', this.availableMicrophones);
        
        // Select the first one by default if none selected
        if (!this.selectedMicrophoneId && this.availableMicrophones.length > 0) {
          this.selectedMicrophoneId = this.availableMicrophones[0].deviceId;
        }
      } catch (error) {
        console.error('❌ Failed to enumerate microphones:', error);
      }
    }
  },
  mounted() {
    // Load Turnstile
    this.loadTurnstile();
    
    // Check question limits
    this.checkQuestionLimits();

    // Check audio recording compatibility
    this.checkAudioCompatibility();
    
    // Note: Microphone enumeration and permission check happens when user starts recording
  },
  watch: {
    activeTab(newTab) {
      // Re-render Turnstile widgets when switching tabs to ensure they're visible
      this.$nextTick(() => {
        if (newTab === 'audio' && !this.turnstileWidgetIdAudio) {
          this.renderTurnstileWidgets();
        } else if (newTab === 'text' && !this.turnstileWidgetId) {
          this.renderTurnstileWidgets();
        }
      });
    }
  },
  beforeUnmount() {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
    }
    // Clean up audio level monitoring
    this.cleanupAudioLevelMonitoring();
    if (this.audioUrl) {
      URL.revokeObjectURL(this.audioUrl);
    }
    if (this.mediaRecorder && this.isRecording) {
      this.stopRecording();
    }
  }
};
</script>

<style scoped>
.question-submission {
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.submission-tabs {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid var(--section-bg);
}

.tab-button {
  flex: 1;
  padding: 1rem;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: #666;
  transition: var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.tab-button:hover {
  color: var(--primary-color);
  background: rgba(30, 136, 229, 0.05);
}

.tab-button.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
}

.tab-icon {
  font-size: 1.2rem;
}

.form-group {
  margin-bottom: 1.5rem;
  position: relative;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  transition: var(--transition-fast);
  background: white;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(30, 136, 229, 0.1);
}

.form-group select:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
}

.char-count {
  position: absolute;
  bottom: 0.5rem;
  right: 0.75rem;
  font-size: 0.875rem;
  color: #999;
}

.recording-section {
  margin: 2rem 0;
  padding: 1.5rem;
  background: var(--section-bg);
  border-radius: 8px;
}

.recording-controls {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 1rem;
}

.record-button,
.stop-button,
.reset-button,
.permission-button {
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.record-button {
  background: var(--primary-color);
  color: white;
}

.record-button:hover:not(:disabled) {
  background: #1976d2;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(30, 136, 229, 0.3);
}

.stop-button {
  background: #e53935;
  color: white;
}

.stop-button:hover {
  background: #c62828;
}

.reset-button {
  background: #666;
  color: white;
}

.reset-button:hover {
  background: #555;
}

.record-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.recording-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  color: #e53935;
  font-weight: 600;
}

.pulse-dot {
  width: 12px;
  height: 12px;
  background: #e53935;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.3);
  }
}

.permission-message {
  text-align: center;
  padding: 1rem;
}

.permission-message p {
  margin-bottom: 1rem;
  color: #666;
}

.permission-button {
  background: var(--primary-color);
  color: white;
}

.permission-button:hover {
  background: #1976d2;
}

.audio-preview {
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
}

.audio-preview label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

.audio-player {
  width: 100%;
  margin-bottom: 0.5rem;
}

.audio-info {
  font-size: 0.875rem;
  color: #666;
  margin: 0;
}

.turnstile-container {
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
  padding: 1rem;
  background: var(--section-bg);
  border-radius: 8px;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

.submit-button {
  padding: 1rem 3rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
}

.submit-button:hover:not(:disabled) {
  background: #1976d2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(30, 136, 229, 0.3);
}

.submit-button:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.dev-notice {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 8px;
  color: #856404;
  font-size: 0.9rem;
  text-align: center;
  font-weight: 600;
}

.dev-note {
  font-size: 0.85rem !important;
  font-style: italic;
  opacity: 0.8;
  margin-top: 0.5rem !important;
}

.question-counter {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f5f5f5;
  border-radius: 8px;
  font-size: 0.9rem;
}

.counter-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.counter-label {
  color: #666;
  font-weight: 500;
}

.counter-value {
  font-weight: 700;
  color: var(--primary-color);
}

.counter-value.limit-warning {
  color: #ff9800;
}

.tab-disabled {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-left: 0.25rem;
}

.tab-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f5f5;
}

.tab-button:disabled:hover {
  background: #f5f5f5;
  color: #666;
}

.limit-message {
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 8px;
  text-align: center;
}

.limit-message h3 {
  color: #856404;
  margin: 0.5rem 0;
}

.limit-message p {
  color: #856404;
  margin: 0.5rem 0 0 0;
}

.limit-icon {
  font-size: 2rem;
}

.compatibility-notice {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.notice-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.compatibility-notice p {
  margin: 0;
  font-size: 0.9rem;
  color: #1565c0;
  line-height: 1.5;
}

.compatibility-notice strong {
  font-weight: 600;
}

.success-message,
.error-message {
  margin-top: 1.5rem;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
}

.success-message {
  background: #e8f5e9;
  border: 2px solid #4caf50;
}

.success-message h3 {
  color: #2e7d32;
  margin: 0.5rem 0;
}

.success-message p {
  color: #1b5e20;
  margin: 0;
}

.success-icon {
  font-size: 2rem;
}

.error-message {
  background: #ffebee;
  border: 2px solid #f44336;
  color: #c62828;
}

.error-icon {
  font-size: 1.5rem;
  margin-right: 0.5rem;
}

/* Audio Level Meter Styles */
.audio-meter-container {
  margin: 20px 0;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #ddd;
}

.audio-meter-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
}

.clipping-warning {
  color: #d32f2f;
  font-weight: 700;
  animation: pulse 0.5s infinite;
}

.quiet-warning {
  color: #f57c00;
  font-weight: 600;
}

.level-ok {
  color: #388e3c;
  font-weight: 600;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.audio-meter-track {
  position: relative;
  width: 100%;
  height: 30px;
  background: linear-gradient(
    to right,
    #388e3c 0%,
    #689f38 40%,
    #fbc02d 70%,
    #f57c00 85%,
    #d32f2f 95%
  );
  border-radius: 4px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
}

.audio-meter-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
  transition: width 50ms ease-out;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
}

.audio-meter-fill.clipping {
  background: rgba(211, 47, 47, 0.4);
}

.audio-meter-fill.quiet {
  background: rgba(245, 124, 0, 0.3);
}

.audio-meter-marks {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.audio-meter-marks .mark {
  position: absolute;
  top: 0;
  width: 2px;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
}

.audio-meter-marks .quiet-threshold {
  left: 15%;
}

.audio-meter-marks .optimal-start {
  left: 40%;
}

.audio-meter-marks .optimal-end {
  left: 70%;
}

.audio-meter-marks .clip-threshold {
  left: 90%;
}

.audio-meter-scale {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  font-size: 11px;
  color: #666;
}

/* Responsive Design */
@media (max-width: 768px) {
  .question-submission {
    padding: 1.5rem;
  }

  .submission-tabs {
    flex-direction: column;
    gap: 0;
  }

  .tab-button {
    border-bottom: 2px solid #e0e0e0;
    border-radius: 0;
  }

  .tab-button.active {
    border-bottom-color: var(--primary-color);
  }

  .recording-controls {
    flex-direction: column;
  }

  .submit-button {
    width: 100%;
  }

  .audio-meter-container {
    padding: 12px;
  }

  .audio-meter-track {
    height: 24px;
  }

  .audio-meter-scale {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .question-submission {
    padding: 1rem;
  }

  .tab-button {
    font-size: 0.9rem;
    padding: 0.75rem;
  }

  .record-button,
  .stop-button,
  .reset-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
